import React, { forwardRef } from 'react';
import Icon, { IconName } from './icons/Icon';
import './InputText.css';

type InputTextVariant = 'default' | 'ghost';

interface InputTextProps {
  /** 見た目のバリエーション */
  variant?: InputTextVariant;
  /** 左アイコンを表示するかどうか */
  hasLeftIcon?: boolean;
  /** 左アイコンの名前 */
  leftIcon?: IconName;
  /** プレースホルダーテキスト */
  placeholder?: string;
  /** デフォルト値 */
  defaultValue?: string;
  /** 値（制御されたコンポーネント用） */
  value?: string;
  /** 変更時のコールバック */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** フォーカス時のコールバック */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** ブラー時のコールバック */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** IME入力開始時のコールバック */
  onCompositionStart?: (event: React.CompositionEvent<HTMLInputElement>) => void;
  /** IME入力終了時のコールバック */
  onCompositionEnd?: (event: React.CompositionEvent<HTMLInputElement>) => void;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー状態 */
  error?: boolean;
  /** 必須項目 */
  required?: boolean;
  /** 入力タイプ */
  type?: 'text' | 'email' | 'password' | 'tel' | 'url';
  /** 名前属性 */
  name?: string;
  /** ID属性 */
  id?: string;
  /** 自動完了 */
  autoComplete?: string;
  /** 自動フォーカス */
  autoFocus?: boolean;
  /** 読み取り専用 */
  readOnly?: boolean;
  /** 最大文字数 */
  maxLength?: number;
  /** 最小文字数 */
  minLength?: number;
  /** パターン */
  pattern?: string;
  /** 追加のクラス名 */
  className?: string;
  /** aria-label */
  'aria-label'?: string;
  /** aria-describedby */
  'aria-describedby'?: string;
}

const InputText = forwardRef<HTMLInputElement, InputTextProps>(({
  variant = 'default',
  hasLeftIcon = false,
  leftIcon = 'user',
  placeholder,
  defaultValue,
  value,
  onChange,
  onFocus,
  onBlur,
  onCompositionStart,
  onCompositionEnd,
  disabled = false,
  error = false,
  required = false,
  type = 'text',
  name,
  id,
  autoComplete,
  autoFocus,
  readOnly,
  maxLength,
  minLength,
  pattern,
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}, ref) => {
  return (
    <div 
      className={`input-text input-text--${variant} ${error ? 'input-text--error' : ''} ${disabled ? 'input-text--disabled' : ''} ${className}`}
    >
      <div className="input-text__state-layer">
        <div className="input-text__container">
          {hasLeftIcon && (
            <div className="input-text__icon">
              <Icon name={leftIcon} size={24} />
            </div>
          )}
          <input
            ref={ref}
            className="input-text__input text-single-large"
            type={type}
            placeholder={placeholder}
            defaultValue={defaultValue}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onCompositionStart={onCompositionStart}
            onCompositionEnd={onCompositionEnd}
            disabled={disabled}
            required={required}
            name={name}
            id={id}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            readOnly={readOnly}
            maxLength={maxLength}
            minLength={minLength}
            pattern={pattern}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={error}
          />
        </div>
      </div>
    </div>
  );
});

InputText.displayName = 'InputText';

export default InputText;
